import { IQuestionTypeName } from "../types";
import { ChildQuestionInput } from "./child_question_input";

/**
 * Class for handling the input for a modal-based question such as the Wall Picker.
 * 
 * @property {number} question_id The id for the question corresponding to the child question.
 * @property {IQuestionTypeName} control_type The type of control ('Dropdown', 'Radio', etc.).
 * @param {HTMLElement} configuration_modal The configuration modal element for the question.
 * @param {HTMLCollection<HTMLLIElement>} config_modal_swatches The swatches of the configuration modal.
 * @param {HTMLElement} config_modal_save_button The save button for the configuration modal.
 * @property {HTMLElement} question_control_element The container element for the child question input.
 * @param {HTMLElement} wall_selected_container Contains controls shown when a wall is selected.
 * @param {HTMLElement} no_wall_container Contains controls shown when no wall is selected.
 * @param {IntersectionObserver} intersection_observer Handles changes to the toc when modal is scrolled.
 */
export class QuestionModal {
    question_id: number;
    control_type: IQuestionTypeName;
    configuration_modal: HTMLElement;
    config_modal_swatches: HTMLCollectionOf<HTMLLIElement>;
    config_modal_save_button: HTMLElement;
    question_control_element: HTMLElement;
    wall_selected_container: HTMLElement;
    no_wall_container: HTMLElement;
    intersection_observer: IntersectionObserver;

    /**
     * Instantiates a QuestionModal.
     *
     * @property {number} question_id The id for the question corresponding to the child question.
     * @property {IQuestionTypeName} control_type The type of control ('Dropdown', 'Radio', etc.).
     */
    constructor(
        question_id: number,
        control_type: string
    ) {
        this.question_id = question_id;
        this.control_type = control_type as IQuestionTypeName;

        this.bind();
    }

    /**
     * Establishes DOM references and attaches event listeners.
     * 
     * @returns {void}
     */
    bind(): void { 
        const modal_id = `tv-configuration-modal-${this.question_id}`;
        // Clone to get rid of any previous event listeners.
        const config_modal = document.getElementById(modal_id);
        this.configuration_modal = config_modal.cloneNode(true) as HTMLElement;
        config_modal.parentNode.replaceChild(this.configuration_modal, config_modal);

        // Modal save and swatch buttons.
        this.config_modal_save_button = this.configuration_modal.querySelector(
            '.js-configuration-modal-save-button'
        )  as HTMLElement;
        this.config_modal_swatches = this.configuration_modal.getElementsByClassName(
            'js-configuration-modal-swatch'
        ) as HTMLCollectionOf<HTMLLIElement>;

        // Dynamically apply flex to wall selector elements.
        const js_wall_selection_items = document.querySelectorAll('.js-wall-selection-item');
        const js_wall_picker_display = document.getElementById('js-wall-picker-display');
        const js_wall_picker_dropdowns = document.querySelectorAll('.js-wall-selection-item .js-wall-picker-dropdown');
        const wall_picker_display = document.createElement('div');
        wall_picker_display.classList.add('tv-margin__top-10');

        Array.prototype.forEach.call(js_wall_selection_items, function(child_element){
            js_wall_picker_display.appendChild(child_element);
        });

        Array.prototype.forEach.call(js_wall_picker_dropdowns, function(child_element){
            if (wall_picker_display.childElementCount === 0) {
                wall_picker_display.insertAdjacentElement('afterbegin', child_element);
                js_wall_picker_display.insertAdjacentElement('afterend', wall_picker_display);
            }
        });

        // Config modal table of contents elements.
        const toc_titles = this.configuration_modal.getElementsByClassName('tv-config-modal-toc-title');
        const toc_headers = this.configuration_modal.getElementsByClassName('tv-config-modal-category-header');
        const category_containers = this.configuration_modal.getElementsByClassName('js-category-container');

        // Use IntersectionObserver to select title in toc as user scrolls.
        const options = {
            root: this.configuration_modal,
            rootMargin: '0px',
            threshold: 0.5
        }
        let visible_categories = new Set<HTMLElement>();
        this.intersection_observer = new IntersectionObserver((entries, observer) => {
            this.scroll_intersection_callback(
                entries,
                category_containers,
                toc_titles,
                visible_categories
            );
        }, options);

        // Bind toc click scroll jump and add observer.
        for (let i = 0; i < toc_titles.length; i++) {
            toc_titles[i].addEventListener('click', this.select_configuration_modal_category.bind(
                this,
                toc_titles,
                toc_titles[i],
                toc_headers[i]
            ));   
            this.intersection_observer.observe(category_containers[i]);
        } 

        for (const config_modal_swatch of this.config_modal_swatches) {
            // Bind config modal swatch click selection event.
            const modal_swatch_listener = this.select_configuration_modal_swatch.bind(
                this,
                config_modal_swatch
            );
            config_modal_swatch.addEventListener('click', modal_swatch_listener);
        }

        // Functionality for color swatch submit button.
        if (this.config_modal_save_button) {
            const save_button_listener = this.save_configuration_modal_selection.bind(
                this
            );
            this.config_modal_save_button.addEventListener('click', save_button_listener);
        }
    }

    /**
     * Handle the entries received by the IntersectionObserver callback 
     * and select the current title in the toc.
     *
     * @param {Array<IntersectionObserverEntry>} entries The entries received by the IntersectionObserver callback. 
     * @param {HTMLCollection} category_containers The containers for each category of the configuration modal.
     * @param {HTMLCollection} titles The titles of the configuration modal toc.
     * @param {Set<HTMLElement>} visible_categories The categories currently visible in the configuration modal.
     * @returns {void}
     */
     scroll_intersection_callback(
        entries: Array<IntersectionObserverEntry>,
        category_containers: HTMLCollection,
        titles: HTMLCollection,
        visible_categories: Set<HTMLElement>
    ): void {
        let top_category = category_containers.length;
        // Keep track of which categories are visible.
        for (const entry of entries) {
            if (entry.isIntersecting) {
                visible_categories.add(entry.target as HTMLElement);
            }
            else {
                visible_categories.delete(entry.target as HTMLElement);
            }
        }

        if (visible_categories.size === 0) {
            return;
        }
        // Select the visible category closest to the top of the modal.
        for (let j = 0; j < category_containers.length; j++) {
            if (visible_categories.has(category_containers[j] as HTMLElement) && j < top_category) {
                top_category = j;
                titles[j].classList.add('tv-config-modal-toc-selected');
            }
            else {
                titles[j].classList.remove('tv-config-modal-toc-selected');
            }
        }
    }

    /**
     * Handle the click events for the table of contents
     * for the configuration modal.
     *
     * @param {HTMLCollection} titles The titles of the configuration modal toc.
     * @param {HTMLElement} current_title The title clicked.
     * @param {HTMLElement} header The corresponding header in the configuration modal.
     * @returns {void}
     */
     select_configuration_modal_category(
        titles: HTMLCollection,
        current_title: HTMLElement,
        header: HTMLElement
    ): void {
        // Exit early if we're already on this category.
        if (current_title.classList.contains('tv-config-modal-toc-selected')) {
            return;
        }

        for (const title of titles) {
            title.classList.remove('tv-config-modal-toc-selected');
        }
        current_title.classList.add('tv-config-modal-toc-selected');
        const current_window_position = window.scrollY
        setTimeout(() => header.scrollIntoView(true), 0);
        window.scrollTo(0, current_window_position);
    }

    /**
     * Handle the click events for the configuration modal save button
     * and dispatch event for the selected advanced select option.
     *
     * @returns {void}
     */
    save_configuration_modal_selection(): void {
        const selected_swatch = this.configuration_modal.querySelector(
            '.tv-config-modal-swatch__selected'
        ) as HTMLLIElement;
        const current_swatches = this.question_control_element.querySelector('.js-current-swatch-selector')?.querySelectorAll(
            '.tv-color-picker__select-option'
        ) as NodeListOf<HTMLElement>;
        // Prevent bubbling so modal won't reopen.
        const select_event = new Event("click", {"bubbles":false});
        // Find the input swatch that matches the modal swatch selected, and click it.
        current_swatches.forEach(current_swatch => {
            if (selected_swatch.value == (current_swatch as HTMLLIElement).value
            ) {
                const advanced_selected_option = this.question_control_element.querySelector('.tv-select__advanced-selected-option-value') as HTMLElement;
                advanced_selected_option.dataset.option_id = (current_swatch as HTMLLIElement).value.toString();
                current_swatch.dispatchEvent(select_event);

                const js_wall_picker_display = document.getElementById('js-wall-picker-display');
                const js_wall_picker_dropdowns = document.querySelectorAll('.js-wall-selection-item .js-wall-picker-dropdown');
                const wall_picker_display = document.createElement('div');
                wall_picker_display.classList.add('tv-margin__top-10');
                Array.prototype.forEach.call(js_wall_picker_dropdowns, function(child_element){
                    if (wall_picker_display.childElementCount === 0) {
                        wall_picker_display.insertAdjacentElement('afterbegin', child_element);
                        js_wall_picker_display.insertAdjacentElement('afterend', wall_picker_display);
                    }
                });
            }
        });
        if (this.wall_selected_container) {
            this.toggle_wall_picker_controls(selected_swatch);
        }
    }

    /**
     * Handle the click events for the configuration modal swatches
     * and their respective 'selected' classes.
     * 
     * @param {HTMLElement} current_swatch The swatch clicked.
     * @returns {void}
     */
     select_configuration_modal_swatch(current_swatch: HTMLElement): void {
        for (const swatch of this.config_modal_swatches) {
            swatch.classList.remove('tv-config-modal-swatch__selected');
            swatch.children[0].classList.add('tv-display__none');
        }
        current_swatch.classList.add('tv-config-modal-swatch__selected');
        current_swatch.children[0].classList.remove('tv-display__none');

        if (current_swatch.classList.contains('color-swatch')) {
            this.config_modal_save_button.classList.remove('tv-disabled__button');
        }
    }

    /**
     * Hides/shows Wall Picker controls based on current selection.
     * 
     * @param {HTMLElement} current_swatch The swatch clicked.
     * @returns {void}
     */
    toggle_wall_picker_controls(current_swatch: HTMLElement): void {
        const swatch_label = current_swatch.parentElement.querySelector('.tv-color-picker__label');
        if (swatch_label.innerHTML == 'No Wall') {
            this.wall_selected_container.classList.add('hidden');
            this.no_wall_container.classList.remove('hidden');
        }
        else {
            this.wall_selected_container.classList.remove('hidden');
            this.no_wall_container.classList.add('hidden');
        }
    }

    /**
     * Removes selected wall from a child question input.
     * 
     * @param {ChildQuestionInput} child_question_input The corresponding child question input.
     * @returns {void}
     */
    remove_wall(child_question_input: ChildQuestionInput) {
        this.set_containers(child_question_input);
        this.save_configuration_modal_selection();
    }

    /**
     * Sets the state of the configuration modal to match a child question input.
     * 
     * @param {ChildQuestionInput} child_question_input The corresponding child question input.
     * @returns {void}
     */
    modal_opened(child_question_input: ChildQuestionInput) {
        this.set_containers(child_question_input);
        // Set initial swatch selection.
        for (const config_modal_swatch of this.config_modal_swatches) {
            if (config_modal_swatch.value === child_question_input.selected_option) {
                this.select_configuration_modal_swatch(config_modal_swatch);
                if (this.wall_selected_container) {
                    this.toggle_wall_picker_controls(config_modal_swatch);
                }
                const current_window_position = window.scrollY
                setTimeout(() => config_modal_swatch.scrollIntoView({ block: "center" }), 0);
                window.scrollTo(0, current_window_position);
            }
        }
    }

    /**
     * Sets the child question input elements for use when saving the modal.
     * 
     * @param {ChildQuestionInput} child_question_input The corresponding child question input.
     * @returns {void}
     */
    set_containers(child_question_input: ChildQuestionInput) {
        const child_question_id = child_question_input.child_question_id;
        const parent_child_relation_id = child_question_input.parent_child_relation_id;
        this.question_control_element =  document.getElementById(
            `js-child-question-${child_question_id}-${parent_child_relation_id}`);
        this.wall_selected_container = this.question_control_element.querySelector('.js-wall-selected-container') as HTMLElement;
        this.no_wall_container = this.question_control_element.querySelector('.js-no-wall-container') as HTMLElement;
    }


}
